import {
  Box,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@material-ui/core";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { ErrorMessage } from "modus/components/error-message/ErrorMessage";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { DeploymentDetails } from "./DeploymentDetails";
import { ThrottlingDetails } from "./ThrottlingDetails";
import { DeploymentCreator } from "../pages/application-summary-page/DeploymentCreator";
import ApiService, {
  IVersionDeployment,
  IVersionDeploymentResponse,
} from "../services/ApiService";
import { getResolvedEnv } from "./EnvBadge";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";
import Skeleton from "@material-ui/lab/Skeleton/Skeleton";
import NoAuthConfig from "./NoAuthConfiguration";
import { DeploymentRemover } from "./DeploymentRemover";
import { ResourceAuthorizer } from "../../authorizer/components/Authorizer";
import { useAuthorization } from "../../authorizer/components/AuthorizationProvider";
import { ApplicationThrottlingDetails } from "./ApplicationThrottlingdetails";
import { ApplicationSummaryPageContext } from "../pages/application-summary-page/ApplicationSummaryPageContext";

interface DeploymentListProps {
  version: string;
  consumerKey: string;
  applicationName: string;
  basePath: string;
  serviceId: string;
  isFlattenedApplication?: boolean;
}

const supportRegionalDeployments =
  process.env.REACT_APP_SUPPORT_REGIONAL_DEPLOYMENTS === "true";

/** Regions still “occupied” for add-deployment UX; failed builds may retry the same region */
const REGION_DEPLOYMENT_FAILURE_STATUSES = new Set([
  "DEPLOY_FAILED",
  "BUILD_FAILED",
  "UPLOAD_FILE_FAILED",
]);

const getFlattenedApiFlag = (versionData: any): boolean => {
  const flagValue = versionData?.isFlattenedApi ?? versionData?.flattenedApi;
  if (typeof flagValue === "boolean") {
    return flagValue;
  }
  if (typeof flagValue === "string") {
    const normalized = flagValue.trim().toLowerCase();
    return normalized === "true";
  }
  return false;
};

const useVersionDeployment = () => {
  const [consumerKey, setConsumerKey] = useState<string>();
  const [version, setVersion] = useState<string>();

  const [state, { subscribe }] = useAsyncAction(() =>
    ApiService.getVersionDeployment(consumerKey as string, version as string)
  );

  const [versionSubscriptionState, { subscribe: subscribeToSubscriptions }] =
    useAsyncAction(() =>
      ApiService.getSubscriptionsForVersion(
        consumerKey as string,
        version as string
      )
    );

  const getVersionDeployment = useCallback((consumerKey, version) => {
    setConsumerKey(consumerKey);
    setVersion(version);
  }, []);

  useEffect(() => {
    if (!consumerKey || !version) {
      return;
    }
    subscribe();
    subscribeToSubscriptions();
  }, [consumerKey, version, subscribeToSubscriptions]);

  return {
    state,
    getVersionDeployment,
    refresh: subscribe,
    versionSubscriptionState,
  };
};

/** Key for deployment row / region dropdown; same precedence regardless of regional feature flag so the UI does not hide controls when only target/gateway fields are populated. */
function deploymentRegionKey(d: IVersionDeploymentResponse): string | undefined {
  const key = d?.targetRegion ?? d?.gatewayRegion ?? d?.location;
  return key === "" ? undefined : key;
}

/** Stable identity for selection / diffing when comparing deployment list snapshots */
function deploymentStableIdentity(d: IVersionDeploymentResponse): string {
  if (d.deploymentId) {
    return `id:${d.deploymentId}`;
  }
  const region = deploymentRegionKey(d);
  return region ? `region:${region}` : "";
}

export const DeploymentList: React.FC<DeploymentListProps> = React.memo(
  ({
    version,
    consumerKey,
    applicationName,
    basePath,
    serviceId,
    isFlattenedApplication = false,
  }) => {
    const {
      notifyDeploymentAddedOnPage,
      notifyDeploymentUpdatedOnPage,
    } = useContext(ApplicationSummaryPageContext);

    const isFedRamp = process.env.REACT_APP_IS_FEDRAMP === "true";

    const isApplicationThrottlingEnabled =
      process.env.REACT_APP_APPLICATION_THROTTLING === "true";

    const [selectedDeployment, setSelectedDeployment] =
      useState<IVersionDeploymentResponse>();

    const [deployments, setDeployments] = useState<IVersionDeploymentResponse[]>(
      []
    );

    const [deployedRegions, setDeployedRegions] = useState<string[]>();

    const [specFile, setSpecFile] = useState<string>();

    const [specId, setSpecId] = useState<string>("");

    const [isFlattenedApi, setIsFlattenedApi] = useState<boolean>(false);

    const [subscribedApps, setSubscribedApps] = useState<Record<string, string>>(
      {}
    );

    const prevDeploymentIdentitiesRef = useRef<Set<string>>(new Set());
    const deploymentListScopeRef = useRef<string>("");

    const changeDeployment = (deployment: IVersionDeploymentResponse) => {
      setSelectedDeployment(deployment);
    };

    const { state, getVersionDeployment, refresh, versionSubscriptionState } =
      useVersionDeployment();

    useEffect(() => {
      if (!version || !consumerKey) {
        return;
      }

      if (state?.value && version === Object.keys(state?.value)[0]) {
        return;
      }
      getVersionDeployment(consumerKey, version);
    }, [version, consumerKey, getVersionDeployment]);

    useEffect(() => {
      if (!state) {
        return;
      }
      if (state?.loaded) {
        const responseData = state?.value as unknown as {
          [key: string]: IVersionDeployment;
        };

        const versionSubscription = versionSubscriptionState?.value as unknown as Record<
          string,
          string
        >;
        const deploymentsArray = responseData?.[version]?.deployments || [];
        setDeployments(deploymentsArray || []);
        setSpecFile(responseData?.[version]?.specFile);
        setSpecId(responseData?.[version]?.specId);
        setIsFlattenedApi(getFlattenedApiFlag(responseData?.[version]));

        setSubscribedApps(versionSubscription || {});
      }
    }, [state, versionSubscriptionState, version]);

    useEffect(() => {
      if (!deployments) {
        return;
      }

      const scopeKey = `${consumerKey}\0${version}`;
      if (deploymentListScopeRef.current !== scopeKey) {
        deploymentListScopeRef.current = scopeKey;
        prevDeploymentIdentitiesRef.current = new Set();
      }

      if (deployments && deployments.length > 0) {
        const prevIdentities = prevDeploymentIdentitiesRef.current;
        const newlyAddedDeployments = deployments.filter((d) => {
          const id = deploymentStableIdentity(d);
          return id !== "" && !prevIdentities.has(id);
        });

        setSelectedDeployment((prev) => {
          if (prevIdentities.size > 0 && newlyAddedDeployments.length > 0) {
            return newlyAddedDeployments[newlyAddedDeployments.length - 1];
          }
          if (prev) {
            if (prev.deploymentId) {
              const byId = deployments.find(
                (d) => d.deploymentId === prev.deploymentId
              );
              if (byId) {
                return byId;
              }
            }
            const prevKey = deploymentRegionKey(prev);
            if (prevKey) {
              const byKey = deployments.find(
                (d) => deploymentRegionKey(d) === prevKey
              );
              if (byKey) {
                return byKey;
              }
            }
          }
          return deployments[0];
        });

        prevDeploymentIdentitiesRef.current = new Set(
          deployments
            .map(deploymentStableIdentity)
            .filter((id) => id !== "")
        );

        if (supportRegionalDeployments) {
          setDeployedRegions(
            deployments
              .filter(
                (d) =>
                  !REGION_DEPLOYMENT_FAILURE_STATUSES.has(d.deployStatus ?? "")
              )
              .map((d) => d?.targetRegion ?? d?.gatewayRegion ?? d?.location)
              .filter((loc): loc is string => loc !== undefined && loc !== "")
          );
        } else {
          setDeployedRegions(
            deployments
              .map((deployment) => deployment?.location)
              .filter((location): location is string => location !== undefined)
          );
        }
      } else {
        setSelectedDeployment(undefined);
        setDeployedRegions([]);
        prevDeploymentIdentitiesRef.current = new Set();
      }
    }, [deployments, version, consumerKey]);

    const { isActionAllowed } = useAuthorization("SERVICE", serviceId);
    const canUpdate = isActionAllowed("update");

    const selectedKey = selectedDeployment
      ? deploymentRegionKey(selectedDeployment)
      : undefined;
    const isDeploymentCreationRestricted =
      isFlattenedApplication || isFlattenedApi;

    return (
      <Box>
        <Paper style={{ width: "100%" }}>
          <Box padding={"20px"}>
            {deployments?.length >= 1 && (
              <Box
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "15px",
                }}
              >
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  Deployment
                </Typography>

                {selectedKey ? (
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    style={{ gap: 15 }}
                  >
                    <Select
                      id="region"
                      value={selectedKey}
                      IconComponent={ExpandMoreIcon}
                      variant="outlined"
                      className="header-selector resource-version-select"
                      style={{
                        height: 40,
                        minHeight: 40,
                      }}
                    >
                      {deployments &&
                        deployments.length > 0 &&
                        deployments.map((deployment, index: number) => {
                          const v = deploymentRegionKey(deployment);
                          if (v === undefined || v === "") {
                            return null;
                          }
                          return (
                            <MenuItem
                              value={v}
                              key={deployment.deploymentId ?? index}
                              onClick={() => changeDeployment(deployment)}
                            >
                              {getResolvedEnv(v as any)}
                            </MenuItem>
                          );
                        })}
                    </Select>
                    {!state?.loading && (
                      <ResourceAuthorizer
                        action={"update"}
                        resourceType={"SERVICE"}
                        resourceId={serviceId}
                        hideErrorMessage
                      >
                        <DeploymentCreator
                          resourceAppName={applicationName}
                          version={version}
                          resourceAppId={consumerKey}
                          refreshDeploymentList={refresh}
                          deployedRegions={deployedRegions}
                          basePath={basePath}
                          isFlattenedApi={isDeploymentCreationRestricted}
                          onDeploymentAdded={
                            supportRegionalDeployments
                              ? notifyDeploymentAddedOnPage
                              : undefined
                          }
                        />
                      </ResourceAuthorizer>
                    )}
                  </Box>
                ) : undefined}
              </Box>
            )}

            {state?.loading && (
              <Skeleton
                animation="wave"
                height="200px"
                width="100%"
                style={{ marginBottom: 6 }}
              />
            )}

            {!state?.loading &&
              deployments &&
              deployments.length > 0 &&
              selectedDeployment && (
                <DeploymentDetails
                  serviceId={serviceId}
                  deploymentId={selectedDeployment?.deploymentId}
                  timeout={String(selectedDeployment?.timeout || "")}
                  deployedEndpoint={selectedDeployment?.deployedUrl || ""}
                  deploymentStatus={selectedDeployment?.deployStatus}
                  location={selectedDeployment?.location}
                  targetRegion={
                    selectedDeployment?.targetRegion ??
                    selectedDeployment?.location ??
                    ""
                  }
                  targetProvider={
                    selectedDeployment?.targetProvider
                  }
                  gatewayRegion={
                    selectedDeployment?.gatewayRegion ??
                    selectedDeployment?.location ??
                    ""
                  }
                  targetEndpoint={selectedDeployment?.backendUrl || ""}
                  refreshDeployments={refresh}
                  onDeploymentUpdated={
                    supportRegionalDeployments
                      ? notifyDeploymentUpdatedOnPage
                      : undefined
                  }
                />
              )}
            {!state?.loading &&
              (version?.length === 0 ||
                deployments === undefined ||
                deployments?.length === 0) && (
                <ErrorMessage
                  message={
                    canUpdate ? (
                      <Box display="flex" flexDirection="column">
                        Add a deployment
                        <Box mt={1} />
                        <DeploymentCreator
                          resourceAppName={applicationName}
                          version={version}
                          resourceAppId={consumerKey}
                          refreshDeploymentList={refresh}
                          deployedRegions={deployedRegions}
                          basePath={basePath}
                          isFlattenedApi={isDeploymentCreationRestricted}
                          onDeploymentAdded={
                            supportRegionalDeployments
                              ? notifyDeploymentAddedOnPage
                              : undefined
                          }
                        />
                      </Box>
                    ) : (
                      <Box display="flex" flexDirection="column">
                        User not authorized to deploy API version.
                      </Box>
                    )
                  }
                />
              )}
          </Box>
          {
            <Paper style={{ width: "100%" }}>
              {deployments?.length >= 1 &&
                selectedDeployment?.deploymentId &&
                !state?.loading && (
                  <ThrottlingDetails
                    serviceId={serviceId}
                    deployStatus={selectedDeployment?.deployStatus}
                    deploymentId={selectedDeployment?.deploymentId}
                    refreshDeploymentList={refresh}
                    apiThrottling={selectedDeployment?.apiThrottling ?? null}
                  ></ThrottlingDetails>
                )}
            </Paper>
          }
          {!isFedRamp && (
            <Paper style={{ width: "100%" }}>
              {deployments?.length >= 1 &&
                selectedDeployment?.deploymentId &&
                !state?.loading &&
                isApplicationThrottlingEnabled && (
                  <ApplicationThrottlingDetails
                    deployStatus={selectedDeployment?.deployStatus}
                    deploymentId={selectedDeployment?.deploymentId}
                    refreshDeploymentList={refresh}
                    applicationThrottling={
                      selectedDeployment?.applicationThrottling as any
                    }
                    subscribedApps={subscribedApps}
                    useApplicationThrottlingV2={supportRegionalDeployments}
                  ></ApplicationThrottlingDetails>
                )}
            </Paper>
          )}

          <Paper style={{ width: "100%" }}>
            {deployments?.length >= 1 &&
              selectedDeployment?.deploymentId &&
              !state?.loading && (
                <NoAuthConfig
                  deployedUrl={selectedDeployment?.deployedUrl}
                  deploymentId={selectedDeployment.deploymentId}
                  refreshDeployments={refresh}
                  noAuthResponse={selectedDeployment?.noAuth || undefined}
                  deploymentStatus={selectedDeployment?.deployStatus}
                  serviceId={serviceId}
                />
              )}
          </Paper>
          {deployments?.length >= 1 && !state?.loading && (
            <Box
              width={"100%"}
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "20px",
              }}
            >
              <ResourceAuthorizer
                action={"delete"}
                resourceType={"SERVICE"}
                resourceId={serviceId}
                hideErrorMessage
              >
                <DeploymentRemover
                  deploymentId={selectedDeployment?.deploymentId || ""}
                  refreshDeployments={refresh}
                  isDisabled={
                    selectedDeployment?.deployStatus === "DEPLOY_INITIATED" ||
                    selectedDeployment?.deployStatus === "FAILED"
                  }
                />
              </ResourceAuthorizer>
            </Box>
          )}
        </Paper>
      </Box>
    );
  }
);
